import { createSlice, createAsyncThunk } from "@reduxjs/toolkit";
import { requestGeoAddress } from "@/lib/geoAddress";

export interface AttendanceRecord {
  date: string;
  user_id: number;
  first_name: string;
  last_name: string | null;
  first_punchin: string;
  last_punchout: string | null;
  duration_seconds: number | null;
  in_latitude: string | number | null;
  in_longitude: string | number | null;
  out_latitude: string | number | null;
  out_longitude: string | number | null;
  in_geo_address: string | null;
  out_geo_address: string | null;
  in_selfie_id: number | null;
  out_selfie_id: number | null;
  status: AttendanceStatus;
}

export interface Employee {
  id: number;
  name: string;
}

export enum AttendanceStatus {
  APPROVED = "APPROVED",
  DELETED = "DELETED",
  PENDING = "PENDING",
  REJECTED = "REJECTED"
} 

interface AttendanceSummaryState {
  records: AttendanceRecord[];
  employees: Employee[];
  loading: boolean;
  employeesLoading: boolean;
  error: string | null;
  geoLoading: Record<string, boolean>;
  geoError: Record<string, string | null>;
  actionLoading: Record<string, boolean>;
}

const initialState: AttendanceSummaryState = {
  records: [],
  employees: [],
  loading: false,
  employeesLoading: false,
  error: null,
  geoLoading: {},
  geoError: {},
  actionLoading: {},
};

export const fetchEmployees = createAsyncThunk(
  "attendanceSummary/fetchEmployees",
  async (_, { rejectWithValue }) => {
    const res = await fetch("/api/admin/employees");
    const data = await res.json();
    if (!res.ok) return rejectWithValue("Failed to fetch employees");
    return (data.employees as any[]).map((emp) => ({
      id: emp.id,
      name: `${emp.first_name}${emp.last_name ? ` ${emp.last_name}` : ""}`,
    })) as Employee[];
  }
);

export interface AttendanceRecordKey {
  userId: string;
  dateOfAttendance: string;
}

export const updateAttendanceStatus = createAsyncThunk(
  "attendanceSummary/update",
  async (
    payload: { records: AttendanceRecordKey[]; status: AttendanceStatus },
    { rejectWithValue }
  ) => {
    const res = await fetch("/api/attendance/summary", {
      method: "PATCH",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify(payload),
    });
    const data = await res.json();
    if (!res.ok) return rejectWithValue(data.error || "Failed to update employee attendance status.");
    return payload;
  }
);

export type StatusFilter = AttendanceStatus | "ALL";

export const fetchAttendanceSummary = createAsyncThunk(
  "attendanceSummary/fetchRecords",
  async (
    { userId, month, status }: { userId: string | number; month: string; status?: StatusFilter },
    { rejectWithValue }
  ) => {
    const statusQuery = status ? `&status=${status}` : "";
    const response = await fetch(
      `/api/attendance/summary?userId=${userId}&month=${month}${statusQuery}`
    );
    const data = await response.json();
    if (!response.ok)
      return rejectWithValue(data.error || "Failed to load attendance records");
    return (Array.isArray(data.records) ? data.records : []) as AttendanceRecord[];
  }
);

export const fetchGeoAddress = createAsyncThunk(
  "attendanceSummary/fetchGeoAddress",
  async (
    {
      date,
      userId,
      type,
    }: {
      date: string;
      userId: number;
      type: "in" | "out";
    },
    { rejectWithValue }
  ) => {
    try {
      const address = await requestGeoAddress({ userId, date, type });
      return { address, date, userId, type };
    } catch (err) {
      return rejectWithValue(err instanceof Error ? err.message : "Failed to fetch address");
    }
  }
);

const attendanceSummarySlice = createSlice({
  name: "attendanceSummary",
  initialState,
  reducers: {},
  extraReducers(builder) {
    builder
      .addCase(fetchEmployees.pending, (state) => {
        state.employeesLoading = true;
      })
      .addCase(fetchEmployees.fulfilled, (state, action) => {
        state.employeesLoading = false;
        state.employees = action.payload;
      })
      .addCase(fetchEmployees.rejected, (state) => {
        state.employeesLoading = false;
      })
      .addCase(updateAttendanceStatus.pending, (state, action) => {
        for (const { userId, dateOfAttendance } of action.meta.arg.records) {
          state.actionLoading[`${dateOfAttendance}_${userId}`] = true;
        }
        state.error = null;
      })
      .addCase(updateAttendanceStatus.fulfilled, (state, action) => {
        const { records, status } = action.payload;
        const keys = new Set(records.map((r) => `${r.dateOfAttendance}_${r.userId}`));
        for (const key of keys) {
          state.actionLoading[key] = false;
        }
        if (status === AttendanceStatus.APPROVED) {
          for (const record of state.records) {
            if (keys.has(`${record.date}_${record.user_id}`)) {
              record.status = status;
            }
          }
        } else {
          state.records = state.records.filter(
            (r) => !keys.has(`${r.date}_${r.user_id}`)
          );
        }
      })
      .addCase(updateAttendanceStatus.rejected, (state, action) => {
        for (const { userId, dateOfAttendance } of action.meta.arg.records) {
          state.actionLoading[`${dateOfAttendance}_${userId}`] = false;
        }
        state.error = (action.payload as string) ?? "Failed to update attendance status";
      })
      .addCase(fetchAttendanceSummary.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(fetchAttendanceSummary.fulfilled, (state, action) => {
        state.loading = false;
        state.records = action.payload;
      })
      .addCase(fetchAttendanceSummary.rejected, (state, action) => {
        state.loading = false;
        state.error = (action.payload as string) ?? "Failed to load attendance records";
      })
      .addCase(fetchGeoAddress.pending, (state, action) => {
        const { date, userId, type } = action.meta.arg;
        const key = `${date}_${userId}_${type}`;
        state.geoLoading[key] = true;
        state.geoError[key] = null;
      })
      .addCase(fetchGeoAddress.fulfilled, (state, action) => {
        const { address, date, userId, type } = action.payload;
        const key = `${date}_${userId}_${type}`;
        state.geoLoading[key] = false;
        const record = state.records.find(
          (r) => r.date === date && r.user_id === userId
        );
        if (record) {
          if (type === "in") record.in_geo_address = address;
          else record.out_geo_address = address;
        }
      })
      .addCase(fetchGeoAddress.rejected, (state, action) => {
        const { date, userId, type } = action.meta.arg;
        const key = `${date}_${userId}_${type}`;
        state.geoLoading[key] = false;
        state.geoError[key] = (action.payload as string) ?? "Failed to fetch address";
      });
  },
});

export default attendanceSummarySlice.reducer;
